import { useReadContracts } from 'wagmi';
import { priceOracleAbi } from '@syntra/abi';
import { DEPLOYMENT as D } from '../config/env.ts';
import { priceGuard, reopensAt } from '../lib/bondLimits.ts';
import { useChainNow } from './useChainClock.tsx';

/**
 * Spot against the average, as the oracle's bond guard sees them, plus how long until the average next moves
 * (the newest checkpoint turning a full period old) while the two disagree: that is when bonds can reopen.
 */
export function usePriceGuard() {
  const q = useReadContracts({
    allowFailure: true,
    query: { refetchInterval: 10_000 },
    contracts: [
      { address: D.oracle, abi: priceOracleAbi, functionName: 'syntraSpotPriceUsd' },
      { address: D.oracle, abi: priceOracleAbi, functionName: 'syntraPriceUsd' },
      { address: D.oracle, abi: priceOracleAbi, functionName: 'maxSpotDeviationBps' },
      { address: D.oracle, abi: priceOracleAbi, functionName: 'checkpoints', args: [false] },
      { address: D.oracle, abi: priceOracleAbi, functionName: 'twapPeriod' },
    ],
  });
  const now = useChainNow(10_000);
  const r = q.data;
  const v = <T,>(i: number) => (r?.[i]?.status === 'success' ? (r[i].result as T) : undefined);
  const spot = v<bigint>(0);
  const twap = v<bigint>(1);
  const guard = priceGuard(spot, twap, v<number>(2));
  const checkpoints = v<readonly [{ timestamp: number }, { timestamp: number }]>(3);
  const period = v<number>(4);
  const reopensIn = guard?.paused && checkpoints && period !== undefined ? Math.max(0, reopensAt(checkpoints[1].timestamp, period) - now) : undefined;
  return { spot, twap, guard, reopensIn };
}
